<template>
  <v-container fluid class="pa-0 ma-0">
    <v-card elevation="0" color="transparent" class="px-5 py-1">

      <v-row v-if="mostrarFiltro" class="d-flex align-center wrap">

        <!-- 1. Empresa Fixa (Obrigatório) -->
        <v-col cols="12" sm="6" md="4" class="py-2">
          <v-select
            v-model="empresaSelecionada"
            :items="filteredEmpresas"
            label="Empresa (Obrigatório)*"
            dense
            solo-inverted
            hide-details
            class="compact-field"
            :loading="loadingDados"
            no-data-text="Nenhuma empresa permitida para o seu usuário"
            background-color="blue-grey lighten-5"
            @change="onEmpresaSelecionada"
          ></v-select>
        </v-col>

        <!-- 2. Departamento Fixo (habilitado apenas após selecionar Empresa) -->
        <v-col cols="12" sm="6" md="4" class="py-2">
          <v-autocomplete
            v-model="departamentoSelecionado"
            :items="entidadesFiltradas"
            item-text="completename"
            item-value="id"
            label="Departamento (Obrigatório)*"
            dense
            solo-inverted
            hide-details
            class="compact-field"
            :loading="loadingDados"
            :disabled="!empresaSelecionada"
            :background-color="empresaSelecionada ? 'blue-grey lighten-5' : 'grey lighten-3'"
            :no-data-text="empresaSelecionada ? 'Nenhum departamento disponível para a sua permissão' : 'Selecione a Empresa primeiro'"
            @change="onDepartamentoSelecionado"
          ></v-autocomplete>
        </v-col>

        <!-- 3. Botão Adicionar Filtro Opcional -->
        <v-col cols="12" sm="12" md="4" class="py-2">
          <v-btn
            @click="adicionarFiltro"
            color="#4682B4"
            small
            class="white--text"
            :disabled="!departamentoSelecionado"
          >
            Adicionar Filtro Opcional <v-icon right small>mdi-plus</v-icon>
          </v-btn>
        </v-col>

        <template v-for="(filtro, index) in filtros">

          <v-col :key="`tipo-${filtro.id}`" cols="12" sm="12" md="3" class="py-1">
            <v-select
              v-model="filtro.tipo"
              :items="tiposDisponiveis(index)"
              label="Selecionar Campo"
              dense
              solo-inverted
              hide-details
              class="compact-field"
              @change="onFiltroChange(index)"
            ></v-select>
          </v-col>

          <!-- Filtro Opcional: Localização -->
          <v-col
            v-if="filtro.tipo === 'Localização'"
            :key="`loc-${filtro.id}`"
            cols="12"
            sm="6"
            md="6"
            class="py-1"
          >
            <v-autocomplete
              v-model="filtro.valor"
              :items="localizacoesDaEmpresa"
              item-text="completename"
              item-value="id"
              label="Selecione a Localização"
              dense
              solo-inverted
              hide-details
              class="compact-field"
              :loading="loadingDados"
              no-data-text="Nenhuma localização disponível"
            ></v-autocomplete>
          </v-col>

          <v-col v-if="filtro.tipo === 'Período'" :key="`data-ini-${filtro.id}`" cols="12" sm="6" md="3" class="py-1">
            <v-menu v-model="filtro.dataInicialAberto" :close-on-content-click="false" transition="scale-transition"
              offset-y min-width="auto">
              <template #activator="{ on, attrs }">
                <v-text-field v-model="filtro.dataInicialFormatada" label="Início" dense solo-inverted readonly
                  hide-details v-bind="attrs" v-on="on" class="compact-field"></v-text-field>
              </template>
              <v-date-picker v-model="filtro.dataInicial" no-title
                @input="filtro.dataInicialAberto = false; filtro.dataInicialFormatada = formatDate(filtro.dataInicial);"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col v-if="filtro.tipo === 'Período'" :key="`data-fim-${filtro.id}`" cols="12" sm="6" md="3" class="py-1">
            <v-menu v-model="filtro.dataFinalAberto" :close-on-content-click="false" transition="scale-transition"
              offset-y min-width="auto">
              <template #activator="{ on, attrs }">
                <v-text-field v-model="filtro.dataFinalFormatada" label="Fim" dense solo-inverted readonly hide-details
                  v-bind="attrs" v-on="on" class="compact-field"></v-text-field>
              </template>
              <v-date-picker v-model="filtro.dataFinal" no-title
                @input="filtro.dataFinalAberto = false; filtro.dataFinalFormatada = formatDate(filtro.dataFinal);"></v-date-picker>
            </v-menu>
          </v-col>

          <v-col v-if="filtro.tipo === 'Categoria'" :key="`cat-${filtro.id}`" cols="12" sm="6" md="6" class="py-1">
            <v-autocomplete
              v-model="filtro.valor"
              :items="listaCategorias"
              item-text="completename"
              item-value="id"
              label="Selecione as Categorias"
              multiple
              chips
              small-chips
              deletable-chips
              dense
              solo-inverted
              hide-details
              class="compact-field"
              :loading="loadingCategorias"
              no-data-text="Selecione o Departamento primeiro"
            ></v-autocomplete>
          </v-col>

          <v-col v-if="filtro.tipo === 'Solicitante'" :key="`solic-${filtro.id}`" cols="12" sm="6" md="6" class="py-1">
            <v-autocomplete
              v-model="filtro.valor"
              :items="listaUsuarios"
              item-text="nomeCompleto"
              item-value="id"
              label="Selecione os Solicitantes"
              multiple
              chips
              small-chips
              deletable-chips
              dense
              solo-inverted
              hide-details
              class="compact-field"
              :loading="loadingDados"
            ></v-autocomplete>
          </v-col>

          <v-col v-if="filtro.tipo === 'Responsável'" :key="`resp-${filtro.id}`" cols="12" sm="6" md="6" class="py-1">
            <v-autocomplete
              v-model="filtro.valor"
              :items="listaUsuarios"
              item-text="nomeCompleto"
              item-value="id"
              label="Selecione o Responsável"
              dense
              solo-inverted
              hide-details
              class="compact-field"
              :loading="loadingDados"
            ></v-autocomplete>
          </v-col>

          <v-col v-if="filtro.tipo === 'Status'" :key="`opt-${filtro.id}`" cols="12" sm="6" md="6" class="py-1">
            <v-select
              v-model="filtro.valor"
              :items="statusOptions"
              label="Selecione os Status"
              multiple
              chips
              small-chips
              deletable-chips
              dense
              solo-inverted
              hide-details
              class="compact-field"
            ></v-select>
          </v-col>

          <v-col :key="`del-${filtro.id}`" cols="12" sm="2" md="1" class="py-1">
            <v-btn @click="removerFiltro(index)" color="error" icon small><v-icon>mdi-close-circle</v-icon></v-btn>
          </v-col>
        </template>
      </v-row>

      <v-card-actions class="justify-end pt-4">
        <v-btn @click="mostrarFiltro = !mostrarFiltro" text color="primary" small>
          {{ mostrarFiltro ? "Ocultar" : "Mostrar Filtros" }} <v-icon right>{{ mostrarFiltro ? "mdi-chevron-up" :
            "mdi-chevron-down" }}</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn @click="limparFiltro" color="grey" text small v-if="mostrarFiltro">Limpar</v-btn>
        <v-btn @click="filtrar" color="#4682B4" class="white--text" small v-if="mostrarFiltro">
          Filtrar <v-icon right small>mdi-filter</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import Glpi from "@/services/glpi";

export default {
  name: "FiltroDinamicoGLPI",
  data() {
    return {
      mostrarFiltro: true,
      loadingDados: false,
      loadingCategorias: false,

      // Lista de todas as Empresas do grupo
      empresas: [
        "A FLOR DO CAFÉ",
        "CEMITERIO PREVIDA",
        "CEMITERIO PREVIDA SÃO MIGUEL",
        "MEMORIAL PARQUE MACEIÓ",
        "PARQUE DAS FLORES",
        "PARQUE DO AGRESTE",
        "PREVIDA",
      ],

      // Empresa e Departamento Selecionados
      empresaSelecionada: null,
      departamentoSelecionado: null,
      entidadesFiltradas: [],
      localizacoesDaEmpresa: [],

      filtros: [],
      tiposDeFiltros: ["Localização", "Categoria", "Solicitante", "Responsável", "Período", "Status"],

      listaLocalizacoes: [],
      listaEntidades: [],
      listaUsuarios: [],
      listaCategorias: [],
      statusOptions: ["Novo", "Em atendimento (atribuído)", "Planejado", "Pendente", "Solucionado", "Fechado"]
    };
  },
  computed: {
    isAdmin() {
      const roles = this.getUserRoles();
      const adminRoles = [
        "ROLE_ADMIN", "ROLE_SUPERADMIN", "ADMIN", "ROLEADMIN", "SUPERADMIN"
      ];
      return roles.some(r => adminRoles.some(adminRole => this.cleanRole(r) === this.cleanRole(adminRole)));
    },

    filteredEmpresas() {
      const permitidas = this.empresas.filter(empresa => this.hasPermission(empresa));
      if (this.isAdmin) {
        return ["COORPORATIVO", ...permitidas];
      }
      return permitidas;
    }
  },
  watch: {
    filteredEmpresas: {
      immediate: true,
      handler(empresas) {
        if (empresas && empresas.length === 1) {
          this.empresaSelecionada = empresas[0];
          this.atualizarLocalizacoesEEntidadesDaEmpresa();
        } else if (empresas && empresas.length > 0 && !this.empresaSelecionada) {
          if (this.isAdmin) {
            this.empresaSelecionada = "COORPORATIVO";
            this.atualizarLocalizacoesEEntidadesDaEmpresa();
          }
        }
      }
    },
    empresaSelecionada(novaEmpresa) {
      if (novaEmpresa) {
        this.atualizarLocalizacoesEEntidadesDaEmpresa();
      }
    }
  },
  mounted() {
    this.carregarDadosIniciais();
  },
  methods: {
    async carregarDadosIniciais() {
      this.loadingDados = true;
      try {
        const [resEntidades, resUsuarios, resLocalizacoes] = await Promise.all([
          Glpi.listar_entidades(),
          Glpi.listar_usuarios(),
          Glpi.listar_localizacoes()
        ]);

        const entidadesRaw = resEntidades.data || [];
        this.listaEntidades = entidadesRaw
          .filter(e => {
            if (!e || !e.completename) return false;
            const norm = this.normalizarTexto(e.completename);
            return norm !== "GRUPO PARQUE" && norm !== "ROOT ENTITY" && norm !== "ENTIDADE RAIZ";
          })
          .map(e => ({
            ...e,
            completename: (e.completename || "").replace(/^Grupo Parque\s*>\s*/i, "").trim()
          }))
          .sort((a, b) => (a.completename || "").localeCompare(b.completename || ""));

        this.listaUsuarios = resUsuarios.data || [];
        this.listaLocalizacoes = resLocalizacoes.data || [];
        this.entidadesFiltradas = [...this.listaEntidades];

        if (this.filteredEmpresas && this.filteredEmpresas.length === 1) {
          this.empresaSelecionada = this.filteredEmpresas[0];
        } else if (this.isAdmin && !this.empresaSelecionada) {
          this.empresaSelecionada = "COORPORATIVO";
        }

        if (this.empresaSelecionada) {
          this.atualizarLocalizacoesEEntidadesDaEmpresa();
        }

      } catch (error) {
        console.error("Erro ao carregar listas:", error);
      } finally {
        this.loadingDados = false;
      }
    },

    getUser() {
      let user = null;
      try {
        const userStr = sessionStorage.getItem("user") || localStorage.getItem("user");
        if (userStr) {
          user = JSON.parse(userStr);
        }
      } catch (e) {
        console.error("Erro ao ler dados do usuário do storage:", e);
      }
      if (!user && this.$store && this.$store.state && this.$store.state.auth && this.$store.state.auth.user) {
        user = this.$store.state.auth.user;
      }
      return user;
    },

    getUserRoles() {
      const user = this.getUser();
      if (!user) return [];
      const extractRoles = (rawRoles) => {
        if (!rawRoles) return [];
        if (typeof rawRoles === "string") return rawRoles.split(",").map(r => r.trim());
        if (Array.isArray(rawRoles)) {
          return rawRoles.map(r => {
            if (typeof r === "string") return r.trim();
            if (typeof r === "object" && r !== null) {
              return (r.name || r.authority || r.role || r.id || "").toString().trim();
            }
            return String(r).trim();
          });
        }
        return [];
      };

      return [
        ...extractRoles(user.roles),
        ...extractRoles(user.authorities),
        ...(user.role ? extractRoles(user.role) : []),
        ...(user.perfil ? extractRoles(user.perfil) : [])
      ].filter(Boolean);
    },

    normalizarTexto(str) {
      return (str || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
        .trim();
    },

    cleanRole(str) {
      return (str || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
    },

    hasPermission(empresa) {
      const roles = this.getUserRoles();
      if (!roles || roles.length === 0) return false;

      // Apenas Administradores do sistema têm acesso a todas as empresas
      const adminRoles = [
        "ROLE_ADMIN", "ROLE_SUPERADMIN", "ADMIN", "ROLEADMIN", "SUPERADMIN"
      ];
      if (roles.some(r => adminRoles.some(adminRole => this.cleanRole(r) === this.cleanRole(adminRole)))) {
        return true;
      }

      if (empresa === "Todas" || empresa === "COORPORATIVO") {
        return false;
      }

      // Mapeamento de ROLEs e variações para cada empresa
      const roleMapping = {
        "PREVIDA": ["ROLE_PREVIDA", "PREVIDA"],
        "PARQUE DAS FLORES": [
          "ROLE_PARQUE_DAS_FLORES", "ROLE_PARQUE DAS FLORES", "ROLE_PARQUE",
          "PARQUE_DAS_FLORES", "PARQUEDASFLORES", "FLORES"
        ],
        "PARQUE DO AGRESTE": [
          "ROLE_PARQUE_DO_AGRESTE", "ROLE_PARQUE DO AGRESTE", 
          "PARQUE_DO_AGRESTE", "PARQUEDOAGRESTE", "AGRESTE"
        ],
        "CEMITERIO PREVIDA": [
          "ROLE_CEMITERIO_PREVIDA", "ROLE_CEMITERIO_PREVIDA_ARA", "ROLE_CEMITERIO PREVIDA", 
          "CEMITERIO_PREVIDA", "CEMITERIOPREVIDA", "CEMITERIO_ARA"
        ],
        "CEMITERIO PREVIDA SÃO MIGUEL": [
          "ROLE_CEMITERIO_PREVIDA_SAO_MIGUEL", "ROLE_CEMITERIO_PREVIDA_SÃO_MIGUEL", 
          "ROLE_CEMITERIO_PREVIDA_SMC", "ROLE_CEMITERIO_PREVIDA_SAO MIGUEL",
          "CEMITERIO_PREVIDA_SAO_MIGUEL", "CEMITERIO_PREVIDA_SMC", 
          "CEMITERIOPREVIDASAOMIGUEL", "CEMITERIOPREVIDASMC", "SAOMIGUEL", "SMC"
        ],
        "MEMORIAL PARQUE MACEIÓ": [
          "ROLE_MEMORIAL_PARQUE_MACEIÓ", "ROLE_MEMORIAL_PARQUE_MACEIO", 
          "ROLE_MEMORIAL_PARQUE_DE_MACEIO", "ROLE_MEMORIAL PARQUE MACEIÓ", "ROLE_MEMORIAL",
          "MEMORIAL_PARQUE_MACEIO", "MEMORIALPARQUEMACEIO", "MEMORIAL"
        ],
        "A FLOR DO CAFÉ": [
          "ROLE_A_FLOR_DO_CAFÉ", "ROLE_A_FLOR_DO_CAFE", "ROLE_A FLOR DO CAFÉ",
          "ROLE_FLOR_DO_CAFE", "ROLE_MELRO_SAMPAIO", "ROLE_MELROSAMPAIO",
          "A_FLOR_DO_CAFE", "AFLORDOCAFE", "FLORDOCAFE", 
          "MELROSAMPAIO", "CAFE"
        ]
      };

      const expectedRoles = roleMapping[empresa] || [
        "ROLE_" + empresa.trim().toUpperCase().replace(/\s/g, "_")
      ];

      return expectedRoles.some(expected => {
        const expectedClean = this.cleanRole(expected);
        return roles.some(userRole => {
          const userClean = this.cleanRole(userRole);
          return userClean === expectedClean ||
                 userClean === `ROLE${expectedClean}` ||
                 userClean.replace(/^ROLE/, "") === expectedClean;
        });
      });
    },

    getUserDepartmentRoles() {
      const roles = this.getUserRoles();
      const nonDepartmentRolesClean = new Set([
        "ROLEADMIN", "ROLESUPERADMIN", "ADMIN", "SUPERADMIN",
        "ROLEGLPI", "GLPI", "ROLEDASHBOARD", "DASHBOARD", "ROLEDASHBOARDADMIN",
        "ROLEUSER", "USER", "ROLEUSERCREATE", "ROLECUPOM", "ROLECUPOMCREATE",
        "ROLEDIGITAL", "ROLEVENDAS", "ROLEAPROVADOS",
        "ROLEPREVIDA", "PREVIDA",
        "ROLEPARQUEDASFLORES", "PARQUEDASFLORES", "ROLEPARQUE", "FLORES",
        "ROLEPARQUEDOAGRESTE", "PARQUEDOAGRESTE", "AGRESTE",
        "ROLECEMITERIOPREVIDA", "CEMITERIOPREVIDA", "ROLECEMITERIOPREVIDAARA", "CEMITERIOARA",
        "ROLECEMITERIOPREVIDASAOMIGUEL", "CEMITERIOPREVIDASAOMIGUEL", "ROLECEMITERIOPREVIDASMC", "CEMITERIOPREVIDASMC", "SAOMIGUEL", "SMC",
        "ROLEMEMORIALPARQUEMACEIO", "MEMORIALPARQUEMACEIO", "ROLEMEMORIAL", "MEMORIAL",
        "ROLEAFLORDOCAFE", "AFLORDOCAFE", "ROLEFLORDOCAFE", "FLORDOCAFE", "ROLEMELROSAMPAIO", "MELROSAMPAIO", "CAFE",
        "ROLECOORPORATIVO", "COORPORATIVO", "ROLETODAS", "TODAS"
      ]);

      return roles.filter(role => {
        const clean = this.cleanRole(role);
        return clean && !nonDepartmentRolesClean.has(clean);
      });
    },

    matchesDepartmentRole(entityName, role) {
      if (!entityName || !role) return false;
      const nameNorm = this.normalizarTexto(entityName);
      const nameClean = this.cleanRole(entityName);
      const roleClean = this.cleanRole(role).replace(/^ROLE/, "");

      if (!roleClean) return false;

      // 1. Comparação direta
      if (nameClean === roleClean || nameClean.startsWith(roleClean) || nameClean.endsWith(roleClean)) {
        return true;
      }

      // 2. Partes separadas por '>' ou ' '
      const parts = nameNorm.split(/[\s>/]+/).map(p => this.cleanRole(p)).filter(Boolean);
      if (parts.includes(roleClean)) {
        return true;
      }

      // 3. Sinônimos conhecidos
      const synonyms = {
        "TI": ["TECNOLOGIA", "INFORMATICA", "SUPORTE", "INFRA", "INFRAESTRUTURA"],
        "RH": ["RECURSOSHUMANOS", "DP", "DEPARTAMENTOPESSOAL", "PESSOAS", "GENTE"],
        "FINANCEIRO": ["FINANCAS", "TESOURARIA", "CONTABILIDADE", "CONTABIL"],
        "OPERACAO": ["OPERACOES", "OPERACIONAL"],
        "OPERACOES": ["OPERACAO", "OPERACIONAL"],
        "COMUNICACAO": ["MARKETING", "MKT"],
        "MARKETING": ["COMUNICACAO", "MKT"],
        "ATENDIMENTO": ["SAC", "CONTACTCENTER", "CALLCENTER"],
        "SAC": ["ATENDIMENTO", "CONTACTCENTER", "CALLCENTER"],
        "RELACIONAMENTO": ["RELACIONAMENTOPREVIDA", "PREVIDARELACIONAMENTO", "ATENDIMENTO"]
      };

      const roleSynonyms = synonyms[roleClean] || [];
      for (const syn of roleSynonyms) {
        if (nameClean === syn || parts.includes(syn) || nameClean.includes(syn)) {
          return true;
        }
      }

      for (const part of parts) {
        const partSynonyms = synonyms[part] || [];
        if (partSynonyms.includes(roleClean)) {
          return true;
        }
      }

      // 4. Substring para roles com mais de 2 letras
      if (roleClean.length >= 3 && nameClean.includes(roleClean)) {
        return true;
      }

      return false;
    },

    matchesEmpresaDepartment(entityName, empresa) {
      if (!entityName || !empresa) return false;
      const nomeNorm = this.normalizarTexto(entityName);

      if (empresa === "COORPORATIVO" || empresa === "Todas") {
        return true;
      }

      const empresaMatchers = {
        "PREVIDA": (norm) => {
          if (norm.includes("CEMITERIO")) return false;
          return (
            norm.includes("PREVIDA") ||
            norm.includes("PREVIPARQ") ||
            norm.includes("FUNERARIA") ||
            norm.includes("VELORIO")
          );
        },
        "PARQUE DAS FLORES": (norm) => {
          if (norm.includes("CEMITERIO") || norm.includes("AGRESTE") || norm.includes("MEMORIAL")) return false;
          return norm.includes("PARQUE DAS FLORES") || norm.includes("FLORES");
        },
        "PARQUE DO AGRESTE": (norm) => {
          return norm.includes("PARQUE DO AGRESTE") || norm.includes("AGRESTE");
        },
        "CEMITERIO PREVIDA": (norm) => {
          if (norm.includes("SMC") || norm.includes("SAO MIGUEL") || norm.includes("SAO MIGUEL DOS CAMPOS")) return false;
          return (
            norm.includes("CEMITERIO PREVIDA ARA") ||
            norm.includes("CEMITERIO PREVIDA ARAPIRACA") ||
            (norm.includes("CEMITERIO") && norm.includes("PREVIDA")) ||
            (norm.includes("CEMITERIO") && norm.includes("ARA"))
          );
        },
        "CEMITERIO PREVIDA SÃO MIGUEL": (norm) => {
          return norm.includes("SMC") || norm.includes("SAO MIGUEL") || (norm.includes("CEMITERIO") && norm.includes("MIGUEL"));
        },
        "MEMORIAL PARQUE MACEIÓ": (norm) => {
          return norm.includes("MEMORIAL");
        },
        "A FLOR DO CAFÉ": (norm) => {
          return (
            norm.includes("FLOR DO CAFE") ||
            norm.includes("FLOR DO CAFÉ") ||
            norm.includes("CAFE") ||
            norm.includes("CAFÉ") ||
            norm.includes("MELRO SAMPAIO")
          );
        }
      };

      const matcher = empresaMatchers[empresa];
      if (matcher) {
        return matcher(nomeNorm);
      }

      const empresaClean = this.cleanRole(empresa);
      return this.cleanRole(entityName).includes(empresaClean);
    },

    atualizarLocalizacoesEEntidadesDaEmpresa() {
      if (!this.empresaSelecionada) {
        this.entidadesFiltradas = [];
        this.localizacoesDaEmpresa = [];
        return;
      }

      if (this.empresaSelecionada === "COORPORATIVO") {
        this.entidadesFiltradas = [...this.listaEntidades];
        this.localizacoesDaEmpresa = [...this.listaLocalizacoes];
        return;
      }

      const matchers = {
        "PREVIDA": (nomeNorm) => {
          if (nomeNorm.includes("CEMITERIO")) return false;
          return (
            nomeNorm.startsWith("PREVIDA") ||
            nomeNorm.includes("PREVIDA") ||
            nomeNorm.includes("VELORIO") ||
            nomeNorm.includes("FUNERARIA") ||
            nomeNorm.includes("PREVIPARQ") ||
            nomeNorm.includes("SEDE") ||
            nomeNorm.includes("ARAPIRACA") ||
            nomeNorm.includes("CENTRO") ||
            nomeNorm.includes("FAROL") ||
            nomeNorm.includes("JACINTINHO") ||
            nomeNorm.includes("PALMEIRA") ||
            nomeNorm.includes("TRAPICHE") ||
            nomeNorm.includes("PATIO") ||
            nomeNorm.includes("SHOPPING") ||
            nomeNorm.includes("SHOPING")
          );
        },
        "PARQUE DAS FLORES": (nomeNorm) => {
          if (nomeNorm.includes("CEMITERIO") || nomeNorm.includes("AGRESTE") || nomeNorm.includes("MEMORIAL")) return false;
          return (
            nomeNorm.includes("PARQUE DAS FLORES") ||
            nomeNorm.includes("FLORES DO PARQUE") ||
            (nomeNorm.includes("PARQUE") && nomeNorm.includes("FLORES"))
          );
        },
        "PARQUE DO AGRESTE": (nomeNorm) => {
          return nomeNorm.includes("PARQUE DO AGRESTE") || nomeNorm.includes("AGRESTE");
        },
        "CEMITERIO PREVIDA": (nomeNorm) => {
          if (nomeNorm.includes("SMC") || nomeNorm.includes("SAO MIGUEL") || nomeNorm.includes("SAO MIGUEL DOS CAMPOS")) return false;
          return (
            nomeNorm.includes("CEMITERIO PREVIDA ARA") ||
            nomeNorm.includes("CEMITERIO PREVIDA ARAPIRACA") ||
            (nomeNorm.includes("CEMITERIO") && nomeNorm.includes("PREVIDA"))
          );
        },
        "CEMITERIO PREVIDA SÃO MIGUEL": (nomeNorm) => {
          return (
            nomeNorm.includes("CEMITERIO PREVIDA SMC") ||
            nomeNorm.includes("CEMITERIO PREVIDA SAO MIGUEL") ||
            nomeNorm.includes("SMC") ||
            (nomeNorm.includes("CEMITERIO") && nomeNorm.includes("MIGUEL"))
          );
        },
        "MEMORIAL PARQUE MACEIÓ": (nomeNorm) => {
          return nomeNorm.includes("MEMORIAL");
        },
        "A FLOR DO CAFÉ": (nomeNorm) => {
          return (
            nomeNorm.includes("FLOR DO CAFE") ||
            nomeNorm.includes("FLOR DO CAFÉ") ||
            nomeNorm.includes("CAFE") ||
            nomeNorm.includes("CAFÉ") ||
            nomeNorm.includes("MELRO SAMPAIO")
          );
        }
      };

      const matcher = matchers[this.empresaSelecionada];
      if (matcher) {
        this.localizacoesDaEmpresa = this.listaLocalizacoes.filter(loc => {
          const nomeNorm = this.normalizarTexto(loc.completename);
          return matcher(nomeNorm);
        });
      } else {
        this.localizacoesDaEmpresa = [...this.listaLocalizacoes];
      }

      // Departamentos disponíveis (filtrados por Role de departamento e Empresa)
      if (this.isAdmin) {
        this.entidadesFiltradas = [...this.listaEntidades];
      } else {
        const deptRoles = this.getUserDepartmentRoles();
        if (deptRoles && deptRoles.length > 0) {
          this.entidadesFiltradas = this.listaEntidades.filter(ent => {
            const matchesRole = deptRoles.some(r => this.matchesDepartmentRole(ent.completename, r));
            const matchesEmpresa = this.matchesEmpresaDepartment(ent.completename, this.empresaSelecionada);
            return matchesRole || matchesEmpresa;
          });
        } else {
          // Quando o usuário não possui role de departamento: libera apenas o departamento com o nome da empresa
          this.entidadesFiltradas = this.listaEntidades.filter(ent => {
            return this.matchesEmpresaDepartment(ent.completename, this.empresaSelecionada);
          });
        }
      }

      // Se houver apenas 1 departamento disponível, auto-seleciona para agilizar
      if (this.entidadesFiltradas.length === 1) {
        this.departamentoSelecionado = this.entidadesFiltradas[0].id;
        this.onDepartamentoSelecionado(this.departamentoSelecionado);
      } else if (this.departamentoSelecionado && !this.entidadesFiltradas.some(e => e.id === this.departamentoSelecionado)) {
        this.departamentoSelecionado = null;
        this.listaCategorias = [];
        this.filtros = [];
      }
    },

    onEmpresaSelecionada() {
      // 1. Limpa o Departamento selecionado, categorias e filtros opcionais
      this.departamentoSelecionado = null;
      this.listaCategorias = [];
      this.filtros = [];

      this.atualizarLocalizacoesEEntidadesDaEmpresa();
    },

    async onDepartamentoSelecionado(entityId) {
      // Sempre que trocar o departamento, limpa filtros opcionais e categorias
      this.filtros = [];
      this.listaCategorias = [];

      if (!entityId) {
        return;
      }
      this.loadingCategorias = true;
      try {
        const response = await Glpi.listar_categorias(entityId);
        this.listaCategorias = response.data || [];
      } catch (error) {
        console.error("Erro ao carregar categorias:", error);
      } finally {
        this.loadingCategorias = false;
      }
    },

    tiposDisponiveis(index) {
      const tiposUsados = this.filtros.filter((_, i) => i !== index).map(f => f.tipo).filter(Boolean);
      return this.tiposDeFiltros.filter(t => !tiposUsados.includes(t));
    },

    adicionarFiltro() {
      this.filtros.push({
        id: Date.now(),
        tipo: null, valor: null,
        dataInicial: null, dataFinal: null,
        dataInicialAberto: false, dataFinalAberto: false,
        dataInicialFormatada: null, dataFinalFormatada: null
      });
    },

    removerFiltro(index) { this.filtros.splice(index, 1); },

    onFiltroChange(index) {
      const f = this.filtros[index];
      f.valor = ['Categoria', 'Solicitante', 'Status'].includes(f.tipo) ? [] : null;
      f.dataInicial = null; f.dataFinal = null;
      f.dataInicialFormatada = null; f.dataFinalFormatada = null;
    },

    limparFiltro() {
      if (this.filteredEmpresas.length > 1) {
        this.empresaSelecionada = null;
        this.entidadesFiltradas = [...this.listaEntidades];
        this.localizacoesDaEmpresa = [];
      } else if (this.filteredEmpresas.length === 1) {
        this.empresaSelecionada = this.filteredEmpresas[0];
        this.atualizarLocalizacoesEEntidadesDaEmpresa();
      }
      this.departamentoSelecionado = null;
      this.listaCategorias = [];
      this.filtros = [];
      this.$emit("filtrar", {});
    },

    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },

    filtrar() {
      if (!this.empresaSelecionada) {
        alert("Atenção: A Empresa é um campo obrigatório!");
        return;
      }

      if (!this.departamentoSelecionado) {
        alert("Atenção: O Departamento é um campo obrigatório!");
        return;
      }

      const values = {
        empresa: this.empresaSelecionada,
        entityId: this.departamentoSelecionado
      };

      // Se a empresa não for corporativo/todas e possuir localizações vinculadas
      if (this.empresaSelecionada !== "COORPORATIVO" && this.localizacoesDaEmpresa && this.localizacoesDaEmpresa.length > 0) {
        values.locationIds = this.localizacoesDaEmpresa.map(l => l.id).filter(Boolean);
      }

      this.filtros.forEach(f => {
        if (!f.tipo) return;

        if (f.tipo === 'Localização') {
          values.locationId = f.valor;
        } else if (f.tipo === 'Período') {
          values.periodoInicial = f.dataInicial;
          values.periodoFinal = f.dataFinal;
        } else if (f.tipo === 'Categoria') {
          if (Array.isArray(f.valor) && f.valor.length > 0) {
            values.categoriaIds = f.valor;
          } else if (f.valor) {
            values.categoriaId = f.valor;
          }
        } else if (f.tipo === 'Solicitante') {
          if (Array.isArray(f.valor) && f.valor.length > 0) {
            values.solicitanteIds = f.valor;
            values.userIds = f.valor;
          } else if (f.valor) {
            values.solicitanteId = f.valor;
            values.userId = f.valor;
          }
        } else if (f.tipo === 'Responsável') {
          values.responsavelId = f.valor;
        } else if (f.tipo === 'Status') {
          if (Array.isArray(f.valor) && f.valor.length > 0) {
            values.statusList = f.valor;
          } else if (f.valor) {
            values.status = f.valor;
          }
        }
      });

      this.$emit("filtrar", values);
    }
  }
};
</script>

<style scoped>
.compact-field {
  font-size: 14px;
}

::v-deep .v-input__control {
  min-height: 40px !important;
}

::v-deep .v-input__slot {
  min-height: 40px !important;
  margin-bottom: 0;
}
</style>